import React from 'react';

import { Badge, Panel, PanelBody, PanelHeader, PanelTitle } from '../../../components/ui';
import { BVEvidenceStrength, BVEvidenceSummary } from '../types';
import { formatDateLabel, formatEvidenceStrength, getEvidenceStrengthBadgeVariant } from '../utils';

interface EvidenceSummaryPanelProps {
  summary: BVEvidenceSummary | null;
  strength?: BVEvidenceStrength | null;
  title?: string;
}

export function EvidenceSummaryPanel({
  summary,
  strength = null,
  title = 'Review coverage',
}: EvidenceSummaryPanelProps) {
  if (!summary) {
    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
        </PanelHeader>
        <PanelBody>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Generate or open a Studio output to inspect review coverage and source-review details.
          </p>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelHeader>
        <div className="flex items-center justify-between gap-3">
          <PanelTitle>{title}</PanelTitle>
          {strength ? (
            <Badge variant={getEvidenceStrengthBadgeVariant(strength)}>
              {formatEvidenceStrength(strength)} support
            </Badge>
          ) : null}
        </div>
      </PanelHeader>
      <PanelBody className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-gray-50 p-3 dark:bg-dark-surface-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Review count</p>
            <p className="mt-1 text-xl font-semibold text-charcoal dark:text-gray-100">
              {summary.review_count.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl bg-gray-50 p-3 dark:bg-dark-surface-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Retailers</p>
            <p className="mt-1 text-sm font-medium text-charcoal dark:text-gray-100">
              {summary.retailer_coverage.length > 0 ? summary.retailer_coverage.join(', ') : 'Not specified'}
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Date range</p>
            <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
              {formatDateLabel(summary.date_range?.from)} to {formatDateLabel(summary.date_range?.to)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Brands used</p>
            <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
              {summary.brands_used.length > 0 ? summary.brands_used.join(', ') : 'Not specified'}
            </p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Products</p>
            <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
              {summary.product_names.length > 0 ? summary.product_names.join(', ') : 'Not specified'}
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Rating distribution</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.keys(summary.rating_distribution).length > 0 ? (
              Object.entries(summary.rating_distribution).map(([label, count]) => (
                <Badge key={label} variant="secondary">
                  {label}: {count}
                </Badge>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No rating distribution returned.</p>
            )}
          </div>
        </div>
      </PanelBody>
    </Panel>
  );
}

export default EvidenceSummaryPanel;
